import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useAuth } from "react-oidc-context";
import { sortHotels, getNextSortDirection } from "../utils/homeUtils";
import { isAbortError, isFresh, isLoadingKey, mergeHotelList, mergeHotelPatches, readCache, runExclusive, setCacheData, setCacheFetched } from "./hotelsCache";

const API_URL = process.env.REACT_APP_API_URL || "/api";

/** Extrait le `result` d'une promesse `allSettled`, en ignorant les annulations. */
const unwrapResult = (settled, message) => {
  if (settled.status === "fulfilled") return settled.value?.data?.result || {};
  if (isAbortError(settled.reason)) return {};
  console.error(message, settled.reason);
  return {};
};

/**
 * @param {object}  options
 * @param {boolean} options.withGreenMilesStays  Charge `nb_greenmiles_stays` via l'endpoint
 *   batch dédié. Désactivé par défaut : la page d'accueil passe `isGeneralList` et ne rend
 *   pas cette colonne. Le compte a été sorti de `/home/list` pour retirer le LEFT JOIN sur
 *   `stays` de la requête bloquante.
 */
export const useHotels = ({ withGreenMilesStays = false } = {}) => {
  // Deux clés distinctes : les deux variantes n'ont pas les mêmes colonnes chargées.
  const cacheKey = withGreenMilesStays ? "home:hotels:with-gm" : "home:hotels";

  const [hotels, setHotels] = useState(() => readCache(cacheKey)?.data ?? []);
  const [loading, setLoading] = useState(() => !readCache(cacheKey));
  // Ids dont les stats sont en vol. Un booléen global re-rendait les 83 lignes à chaque
  // bascule, et vidait les 82 autres lignes quand on rafraîchissait un seul hôtel.
  const [statsPendingIds, setStatsPendingIds] = useState(() => new Set());
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortState, setSortState] = useState({ column: null, direction: "asc" });

  const auth = useAuth();

  // Le token est renouvelé silencieusement en cours de session. Le lire via une ref au
  // moment de la requête — et non via les dépendances — évite qu'un renouvellement change
  // l'identité des callbacks, invalide le cache et relance tous les chargements.
  const tokenRef = useRef(auth.user?.access_token);
  useEffect(() => {
    tokenRef.current = auth.user?.access_token;
  });

  // Miroir de `hotels` : permet de fusionner sans passer par un updater fonctionnel, donc
  // de connaître la liste résultante tout de suite (pour le cache) et de sauter `setHotels`
  // quand la fusion ne change rien.
  const hotelsRef = useRef(hotels);

  const requestConfig = useCallback(
    (signal, extra) => ({
      headers: { Authorization: `Bearer ${tokenRef.current}` },
      ...(signal ? { signal } : {}),
      ...extra,
    }),
    []
  );

  /**
   * Point de passage unique pour modifier la liste : tient à jour la ref, le cache, et
   * n'appelle `setHotels` que si la référence change réellement.
   */
  const commitHotels = useCallback(
    (updater, { fetched = false } = {}) => {
      const previous = hotelsRef.current;
      const next = typeof updater === "function" ? updater(previous) : updater;

      hotelsRef.current = next;
      if (fetched) {
        setCacheFetched(cacheKey, next);
      } else {
        setCacheData(cacheKey, next);
      }

      if (next !== previous) setHotels(next);
    },
    [cacheKey]
  );

  const markStatsPending = useCallback((hotelIds) => {
    setStatsPendingIds((previous) => {
      const next = new Set(previous);
      hotelIds.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const clearStatsPending = useCallback((hotelIds) => {
    setStatsPendingIds((previous) => {
      if (previous.size === 0) return previous;
      const next = new Set(previous);
      hotelIds.forEach((id) => next.delete(id));
      return next.size === previous.size ? previous : next;
    });
  }, []);

  /**
   * Charge les stats d'un sous-ensemble d'hôtels et les fusionne.
   * Utilisé par les actions unitaires (mise à jour de statut, snapshot) ; le chargement
   * initial passe par `loadHotels`, qui fusionne stats et séjours en une seule fois.
   */
  const fetchHotelsStats = useCallback(
    async (hotelIds) => {
      if (!hotelIds || hotelIds.length === 0) return;

      markStatsPending(hotelIds);
      try {
        const response = await axios.post(`${API_URL}/home/stats`, { hotelIds }, requestConfig());
        commitHotels((previous) => mergeHotelPatches(previous, response.data.result || {}));
      } catch (error) {
        if (isAbortError(error)) return;
        console.error("Erreur lors de la récupération des stats des hôtels:", error);
        // Ne pas bloquer l'affichage si les stats échouent
      } finally {
        clearStatsPending(hotelIds);
      }
    },
    [commitHotels, markStatsPending, clearStatsPending, requestConfig]
  );

  /**
   * Chargement complet : la liste d'abord (affichage immédiat), puis les enrichissements
   * en parallèle, fusionnés en **une seule** mise à jour d'état.
   *
   * @param {AbortSignal} signal      annule tout au démontage
   * @param {boolean}     background  revalidation silencieuse : pas d'indicateur de
   *                                  chargement, la liste affichée n'est jamais vidée
   */
  const loadHotels = useCallback(
    async ({ signal, background = false } = {}) => {
      // Une revalidation de fond ne se superpose pas à un chargement déjà en vol.
      if (background && isLoadingKey(cacheKey)) return;

      if (!background) setLoading(true);
      setError(null);

      try {
        await runExclusive(cacheKey, async () => {
          const response = await axios.get(`${API_URL}/home/list`, requestConfig(signal));
          if (signal?.aborted) return;

          const list = response.data.result || [];
          const alreadyDisplayedIds = new Set(hotelsRef.current.map((hotel) => hotel.id));

          // En revalidation, on conserve les objets inchangés et les stats déjà chargées.
          commitHotels((previous) => (previous.length > 0 ? mergeHotelList(previous, list) : list), { fetched: true });
          setLoading(false);

          if (list.length === 0) return;

          const hotelIds = list.map((hotel) => hotel.id);
          // Une revalidation de fond ne doit pas vider les colonnes déjà affichées : seules
          // les lignes réellement nouvelles passent en placeholder.
          const pendingIds = background ? hotelIds.filter((id) => !alreadyDisplayedIds.has(id)) : hotelIds;
          markStatsPending(pendingIds);

          try {
            const [statsSettled, staysSettled] = await Promise.allSettled([
              axios.post(`${API_URL}/home/stats`, { hotelIds }, requestConfig(signal)),
              withGreenMilesStays
                ? axios.get(`${API_URL}/home/greenmiles-stays`, requestConfig(signal, { params: { hotelIds: hotelIds.join(",") } }))
                : Promise.resolve(null),
            ]);
            if (signal?.aborted) return;

            const statsMap = unwrapResult(statsSettled, "Erreur lors de la récupération des stats des hôtels:");
            const staysMap = staysSettled.value === null ? {} : unwrapResult(staysSettled, "Erreur lors de la récupération des séjours GreenMiles:");

            // Fusion unique : un seul rendu pour les deux réponses.
            const patches = {};
            hotelIds.forEach((id) => {
              const patch = { ...(statsMap[id] || {}), ...(staysMap[id] || {}) };
              if (Object.keys(patch).length > 0) patches[id] = patch;
            });

            commitHotels((previous) => mergeHotelPatches(previous, patches), { fetched: true });
          } finally {
            clearStatsPending(pendingIds);
          }
        });
      } catch (error) {
        if (isAbortError(error)) return;
        console.error("Erreur lors de la récupération de la liste des hôtels:", error);
        setError("Erreur lors du chargement des hôtels");
        setLoading(false);
      }
    },
    [cacheKey, commitHotels, markStatsPending, clearStatsPending, requestConfig, withGreenMilesStays]
  );

  // Chargement au montage. Toutes les dépendances sont stables : plus besoin de mentir sur
  // le tableau de dépendances, et un renouvellement de token ne relance plus rien.
  useEffect(() => {
    const controller = new AbortController();
    const entry = readCache(cacheKey);

    if (entry) {
      // Retour sur la page : affichage immédiat, revalidation seulement si périmé.
      setLoading(false);
      if (!isFresh(entry)) loadHotels({ signal: controller.signal, background: true });
    } else {
      loadHotels({ signal: controller.signal });
    }

    return () => controller.abort();
  }, [cacheKey, loadHotels]);

  /** Rechargement explicite (retour d'une modale d'édition) : ignore la fraîcheur. */
  const fetchHotels = useCallback(async () => {
    await loadHotels();
  }, [loadHotels]);

  // managerId = id manager (pour les scripts), masterId = id master lunidata_v2 (pour refetch stats)
  const updateHotelStatus = useCallback(
    async (managerId, masterId) => {
      try {
        await axios.post(`${API_URL}/scripts/updateStatus/hotel/${managerId}`, {}, requestConfig());
        await fetchHotelsStats([masterId ?? managerId]);
      } catch (error) {
        console.error("Erreur lors de la mise à jour des statuts:", error);
        throw error;
      }
    },
    [fetchHotelsStats, requestConfig]
  );

  const generateSnapshot = useCallback(
    async (managerId, masterId) => {
      try {
        await axios.post(`${API_URL}/scripts/snapshot/hotel/${managerId}`, {}, requestConfig());
        await fetchHotelsStats([masterId ?? managerId]);
      } catch (error) {
        console.error("Erreur lors de la génération du snapshot:", error);
        throw error;
      }
    },
    [fetchHotelsStats, requestConfig]
  );

  const updateFeatureStatus = useCallback(
    async (masterId, feature, status) => {
      try {
        let endpoint;
        if (feature === "lunishower") {
          endpoint = "lunishower-status";
        } else if (feature === "greenmiles") {
          endpoint = "greenmiles-status";
        } else if (feature === "lunibutton") {
          endpoint = "lunibutton-status";
        } else {
          throw new Error("Feature inconnue");
        }

        await axios.patch(`${API_URL}/hotels/${masterId}/${endpoint}`, { status }, requestConfig());

        // Mettre à jour localement sans recharger toute la page : seule la ligne concernée
        // change de référence, les 82 autres ne sont pas re-rendues.
        commitHotels((previous) => mergeHotelPatches(previous, { [masterId]: { [`${feature}_status`]: status } }));
      } catch (error) {
        console.error("Erreur lors de la mise à jour du statut de la feature:", error);
        throw error;
      }
    },
    [commitHotels, requestConfig]
  );

  const updateTag = useCallback(
    async (masterId, tag) => {
      try {
        await axios.patch(`${API_URL}/hotels/${masterId}/tag`, { tag }, requestConfig());
        commitHotels((previous) => mergeHotelPatches(previous, { [masterId]: { tag } }));
      } catch (error) {
        console.error("Erreur lors de la mise à jour du tag:", error);
        throw error;
      }
    },
    [commitHotels, requestConfig]
  );

  const handleSort = useCallback((column) => {
    setSortState((previous) => ({ column, direction: getNextSortDirection(previous.column, column, previous.direction) }));
  }, []);

  // Filtrer les hôtels en local selon la recherche
  const filteredHotels = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return hotels;

    return hotels.filter((hotel) => {
      const name = hotel.name?.toLowerCase() || "";
      const tag = hotel.tag?.toLowerCase() || "";
      return name.includes(term) || tag.includes(term);
    });
  }, [hotels, searchTerm]);

  // Appliquer le tri aux hôtels filtrés. Mémoïsé comme `filteredHotels` juste au-dessus :
  // sans cela, tout le tableau était retrié à chaque render (y compris à chaque frappe).
  const sortedHotels = useMemo(() => sortHotels(filteredHotels, sortState.column, sortState.direction), [filteredHotels, sortState.column, sortState.direction]);

  return {
    hotels: sortedHotels,
    loading,
    // Indicateur global de la page, inchangé.
    loadingStats: statsPendingIds.size > 0,
    // Détail par hôtel, pour que seules les lignes concernées affichent un placeholder.
    statsPendingIds,
    error,
    searchTerm,
    setSearchTerm,
    fetchHotels,
    updateHotelStatus,
    generateSnapshot,
    updateFeatureStatus,
    updateTag,
    sortState,
    handleSort,
  };
};
